/**
 * Manage flats / societies (multi-society). Lists every membership the signed-in
 * person holds — approved, pending or rejected — with a status pill, and lets
 * them add another flat (→ /account/add-flat) or leave/withdraw one. This is how
 * a resident gains a second society; once a request is approved it shows up in
 * the top-bar SocietySwitcher.
 */
import { router } from 'expo-router';
import React from 'react';
import { View } from 'react-native';

import { space } from '@bero/tokens';
import { Icon } from '@/features/account-admin/icons';
import { useFetch } from '@/features/community/lib';
import { api } from '@/lib/client';
import { useSession } from '@/lib/session';
import { useT } from '@/i18n';
import { useTheme } from '@/theme';
import { AppText, Button, EmptyState, Hairline, Pill, Screen, Skeleton, TopBar } from '@/ui';
import type { PillStatus } from '@bero/tokens';

type Membership = {
  id: number; societyId: number; societyName?: string; city?: string;
  status: 'PENDING' | 'APPROVED' | 'REJECTED'; residentType: string;
  flat?: { code: string };
};

const STATUS_TONE: Record<Membership['status'], PillStatus> = {
  APPROVED: 'approved', PENDING: 'pending', REJECTED: 'rejected',
};

async function fetchFlats(): Promise<Membership[]> {
  const body = await api.get<{ items: Membership[] }>('/api/me/flats');
  return Array.isArray(body.items) ? body.items : [];
}

function Row({ m, onRemove, busy }: { m: Membership; onRemove: () => void; busy: boolean }) {
  const { colors } = useTheme();
  const { t } = useT();
  return (
    <View style={{ paddingVertical: space.s3, paddingHorizontal: space.s2, gap: space.s2 }}>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: space.s3 }}>
        <View style={{ flex: 1, minWidth: 0 }}>
          <AppText variant="bodyMd" weight="600">{m.societyName ?? t('mfl_a_society')}</AppText>
          <AppText variant="bodySm" tone="tertiary">
            {[m.flat?.code, m.residentType ? m.residentType.toLowerCase() : '', m.city]
              .filter(Boolean).join(' · ')}
          </AppText>
        </View>
        <Pill status={STATUS_TONE[m.status]} label={t(`mfl_status_${m.status.toLowerCase()}` as 'mfl_status_pending')} />
      </View>
      <View style={{ flexDirection: 'row' }}>
        <Button
          title={m.status === 'PENDING' ? t('mfl_withdraw') : t('mfl_leave')}
          kind="tertiary"
          loading={busy}
          onPress={onRemove}
          style={{ alignSelf: 'flex-start', paddingHorizontal: 0 }}
        />
      </View>
    </View>
  );
}

export default function ManageFlatsScreen() {
  const { colors } = useTheme();
  const { t } = useT();
  const { signOut } = useSession();
  const { data, loading, error, refetch } = useFetch(fetchFlats, []);
  // This screen is also the landing state for a signed-in person with no
  // approved society yet (home guard → here). In that state there is no
  // screen behind it, so: no dead back-chevron, and an explicit Sign out so
  // nobody is ever trapped while waiting for approval.
  const canGoBack = router.canGoBack();
  const leave = async () => { await signOut(); router.replace('/login'); };
  const rows = data ?? [];
  const [busyId, setBusyId] = React.useState<number | null>(null);

  const remove = async (id: number) => {
    if (busyId) return;
    setBusyId(id);
    try {
      await api.delete(`/api/me/flats/${id}`);
      await refetch();
    } catch {
      // leave the row; a transient failure just means try again
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Screen scroll={false}>
      <TopBar
        title={t('mfl_title')}
        onBack={canGoBack ? () => router.back() : undefined}
        right={<Button title={t('sign_out')} kind="tertiary" onPress={() => void leave()} />}
      />
      <Screen padded>
        <Button
          title={t('mfl_add_flat')}
          onPress={() => router.push('/account/add-flat')}
          style={{ marginBottom: space.s5 }}
        />
        {loading ? (
          <View style={{ gap: space.s2 }}>{[0, 1].map((i) => <Skeleton key={i} h={72} />)}</View>
        ) : error ? (
          <EmptyState
            icon={<Icon name="alert-triangle" size={40} color={colors.textTertiary} />}
            title={t('mfl_err')} cta={t('retry')} onCta={() => void refetch()}
          />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Icon name="home" size={40} color={colors.textTertiary} />}
            title={t('mfl_empty_title')} body={t('mfl_empty_body')}
          />
        ) : (
          <View>
            {rows.map((m, i) => (
              <View key={m.id}>
                <Row m={m} busy={busyId === m.id} onRemove={() => void remove(m.id)} />
                {i < rows.length - 1 ? <Hairline /> : null}
              </View>
            ))}
          </View>
        )}
      </Screen>
    </Screen>
  );
}
